import { Injectable } from '@angular/core';
import { produce, Patch } from 'immer';
import { cloneDeep } from 'lodash';
import { DropSettings } from '../models/drop-settings';
import { PlanogramDrop } from '../models/layout/planogram-drop';
import { PlanogramLayout } from '../models/layout/planogram-layout';
import { PlanogramProduct } from '../models/layout/planogram-product';
import { PlanogramProductStack } from '../models/layout/planogram-product-stack';
import { PlanogramShelf } from '../models/layout/planogram-shelf';
import { ProductOrientation } from '../models/layout/product-orientation';
import { ProductReference } from '../models/layout/product-reference';
import { ShelfType } from '../models/layout/shelf-type';
import { LayoutUtilitiesService } from '../utilities/layout.utilities';
import { ClipboardService } from './clipboard.service';
import { LayoutHistoryService } from './layout-history.service';
import { LayoutMetricsService } from './layout-metrics.service';
import { LayoutQueryService } from './layout-query.service';
import { LayoutStateService } from './layout-state.service';
import { PlanogramService } from './planogram.service';
import {
  PlanogramValidationService,
  ValidationError,
} from './planogram-validation.service';

/**
 * Step 5a of the LayoutService split: simple mutators (those built on the
 * `mutateLayout` helper, plus the dragging flag setters and the not-yet-
 * implemented *ById stubs).
 *
 * Owns the shared `updateLayout` / `mutateLayout` helpers — they are public
 * so the validation-heavy mutators still on LayoutService (pending Step 5b)
 * can route through a single source of truth for snapshot/breakdown/publish.
 */
@Injectable({
  providedIn: 'root',
})
export class LayoutMutationService {
  constructor(
    private state: LayoutStateService,
    private metrics: LayoutMetricsService,
    private history: LayoutHistoryService,
    private query: LayoutQueryService,
    private clipboardService: ClipboardService,
    private validationService: PlanogramValidationService,
    private planogramService: PlanogramService,
    private layoutUtils: LayoutUtilitiesService
  ) {}

  /**
   * Boilerplate wrapper for mutators. Runs `mutate` against an immer draft of
   * the current layout snapshot, captures the inverse+forward patches, hands
   * them to LayoutHistoryService for undo/redo, and publishes the produced
   * layout.
   *
   * Pass `shelf` to recompute that shelf's metrics; pass `drop` (with or
   * without `shelf`) to recompute that drop's metrics. Omit both for
   * mutations that don't need a metric recompute (e.g. plain addDrop) or
   * for mutators that recompute metrics themselves inside `mutate`.
   *
   * A throw from `mutate` (e.g. ValidationError) propagates without
   * publishing — immer aborts the production, no patches are recorded, and
   * the layout snapshot is untouched.
   */
  private mutateLayout(
    mutate: (draft: PlanogramLayout) => void,
    opts: { drop?: number; shelf?: number; skipUndo?: boolean } = {}
  ): void {
    const current = this.state.getLayoutValue();
    if (!current) return;

    let forward: Patch[] = [];
    let inverse: Patch[] = [];
    const next = produce(
      current,
      (draft) => {
        mutate(draft);
        if (opts.drop !== undefined && opts.shelf !== undefined) {
          this.metrics.updateShelfMetrics(draft, opts.drop, opts.shelf);
        }
        if (opts.drop !== undefined) {
          this.metrics.updateDropMetrics(draft, opts.drop);
        }
      },
      (p, ip) => {
        forward = p;
        inverse = ip;
      }
    );

    this.history.recordPatches(inverse, forward, opts.skipUndo ?? false);
    this.metrics.updateLayoutBreakdown(next);
    this.metrics.updateLayoutDOSBreakdown(next);
    this.metrics.updateLayoutDFacingsBreakdown(next);
    this.metrics.updateLayoutCapacityBreakdown(next);
    this.state.publishLayout(next);
  }

  /** Publishes an in-place mutation of the live layout snapshot without
   *  recording an undo step. Used by dragging-flag setters that intentionally
   *  bypass undo. Breakdown is intentionally skipped — dragging-flag changes
   *  don't affect product counts or DOS. */
  private publishLive(layout: PlanogramLayout) {
    this.state.publishLayout(layout);
  }

  // Drop Operations
  addDrop(index: number) {
    this.mutateLayout((layout) => {
      layout.drops.splice(index, 0, this.buildEmptyDrop());
    });
  }

  addDropBefore(index: number) {
    this.addDrop(index);
  }

  addDropAfter(index: number) {
    this.addDrop(index + 1);
  }

  removeDrop(index: number) {
    this.mutateLayout((layout) => {
      layout.drops.splice(index, 1);
    });
  }

  private buildEmptyDrop(): PlanogramDrop {
    return {
      shelves: [],
      totalFilledHeight: 0,
      height: this.planogramService.getDropHeight(),
      width: this.planogramService.getShelfWidth(),
      depth: this.planogramService.getDropDepth(),
      footerHeight: this.planogramService.getDropFooterHeight(),
      lipHeight: this.planogramService.getShelfLipHeight(),
      minSpacing: this.planogramService.getShelfMinimumSpacing(),
    };
  }

  clearDrop(dropIndex: number) {
    this.mutateLayout((layout) => {
      layout.drops[dropIndex].shelves = [];
    });
  }

  // Shelf Operations
  removeShelf(dropIndex: number, shelfIndex: number) {
    this.mutateLayout(
      (layout) => {
        layout.drops[dropIndex].shelves.splice(shelfIndex, 1);
      },
      { drop: dropIndex }
    );
  }

  clearShelf(dropIndex: number, shelfIndex: number) {
    this.mutateLayout(
      (layout) => {
        const shelf = layout.drops[dropIndex]?.shelves[shelfIndex];
        if (!shelf) return;
        shelf.stacks = [];
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  createShelf(dropIndex: number) {
    this.mutateLayout((layout) => {
      const newShelf: PlanogramShelf = {
        stacks: [],
        topShelf: false,
        filledWidth: 0,
        shelfHeight: 0,
        shelfSpacing: 0,
        shelfType: ShelfType.REGULAR,
      };
      layout.drops[dropIndex].shelves.push(newShelf);
    });
  }

  // Product Operations
  removeProduct(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ) {
    this.mutateLayout(
      (layout) => {
        const shelf = layout.drops[dropIndex]?.shelves[shelfIndex];
        if (!shelf) return;
        const stack = shelf.stacks[stackIndex];
        if (!stack) return;

        stack.products.splice(productIndex, 1);
        if (stack.products.length === 0) {
          shelf.stacks.splice(stackIndex, 1);
        } else {
          this.metrics.updateProductPositions(stack);
        }
        this.metrics.recalculateStackPositions(layout, dropIndex, shelfIndex);
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  // Facing Operations
  removeHFacing(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ) {
    this.mutateLayout(
      (layout) => {
        const shelf = layout.drops[dropIndex]?.shelves[shelfIndex];
        if (!shelf) return;
        const stack = shelf.stacks[stackIndex];
        if (!stack) return;

        stack.products.splice(productIndex, 1);
        if (stack.products.length === 0) {
          shelf.stacks.splice(stackIndex, 1);
        }
        this.metrics.recalculateStackPositions(layout, dropIndex, shelfIndex);
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  addVFacing(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ) {
    this.mutateLayout(
      (layout) => {
        const drop = layout.drops[dropIndex];
        const product =
          drop?.shelves[shelfIndex]?.stacks[stackIndex]?.products[productIndex];
        if (!drop || !product) return;
        const productRef = layout.productReference.get(product.productId);
        if (!productRef) return;

        // Simulate new stack/shelf height for validation; on success, apply.
        const tempDrop = cloneDeep(drop);
        tempDrop.shelves[shelfIndex].stacks[stackIndex].products[
          productIndex
        ].stackHeight++;
        this.validationService.validateDropHeight(tempDrop, layout);

        product.stackHeight = product.stackHeight + 1;
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  removeVFacing(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ) {
    this.mutateLayout(
      (layout) => {
        const product =
          layout.drops[dropIndex]?.shelves[shelfIndex]?.stacks[stackIndex]
            ?.products[productIndex];
        if (!product) return;
        if (product.stackHeight > 1) {
          product.stackHeight -= 1;
        }
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }


  changeProductOrientation(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number,
    orientation: ProductOrientation
  ) {
    this.mutateLayout(
      (layout) => {
        const product =
          layout.drops[dropIndex]?.shelves[shelfIndex]?.stacks[stackIndex]
            ?.products[productIndex];
        if (!product) return;
        const productRef = this.state.planogramItems.get(product.productId);
        if (!productRef) return;

        product.orientation = orientation;
        product.dimensions =
          this.layoutUtils.calculateEffectiveDimensions(
            productRef,
            product
          );
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  // Stack order operations
  moveProductUpInStack(d: number, s: number, k: number, p: number) {
    this.moveProductInStack(d, s, k, p, 'up');
  }

  moveProductDownInStack(d: number, s: number, k: number, p: number) {
    this.moveProductInStack(d, s, k, p, 'down');
  }

  moveProductToTopOfStack(d: number, s: number, k: number, p: number) {
    this.moveProductInStack(d, s, k, p, 'top');
  }

  moveProductToBottomOfStack(d: number, s: number, k: number, p: number) {
    this.moveProductInStack(d, s, k, p, 'bottom');
  }

  private moveProductInStack(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number,
    direction: 'up' | 'down' | 'top' | 'bottom'
  ) {
    this.mutateLayout(
      (layout) => {
        const stack =
          layout.drops[dropIndex]?.shelves[shelfIndex]?.stacks[stackIndex];
        if (!stack) return;

        const lastIndex = stack.products.length - 1;
        const atTop = productIndex === 0;
        const atBottom = productIndex === lastIndex;
        if ((direction === 'up' || direction === 'top') && atTop) return;
        if ((direction === 'down' || direction === 'bottom') && atBottom)
          return;

        if (direction === 'up' || direction === 'down') {
          const swapWith =
            direction === 'up' ? productIndex - 1 : productIndex + 1;
          const temp = stack.products[productIndex];
          stack.products[productIndex] = stack.products[swapWith];
          stack.products[swapWith] = temp;
        } else {
          const [product] = stack.products.splice(productIndex, 1);
          if (direction === 'top') {
            stack.products.unshift(product);
          } else {
            stack.products.push(product);
          }
        }

        this.metrics.updateProductPositions(stack);
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  // Removal operations
  removeStack(dropIndex: number, shelfIndex: number, stackIndex: number) {
    this.mutateLayout(
      (layout) => {
        const shelf = layout.drops[dropIndex]?.shelves[shelfIndex];
        if (!shelf) return;
        shelf.stacks.splice(stackIndex, 1);
        this.metrics.recalculateStackPositions(layout, dropIndex, shelfIndex);
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  clearStackAbove(d: number, s: number, k: number, p: number) {
    this.clearStackRange(d, s, k, p, 'above');
  }

  clearStackBelow(d: number, s: number, k: number, p: number) {
    this.clearStackRange(d, s, k, p, 'below');
  }

  private clearStackRange(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number,
    direction: 'above' | 'below'
  ) {
    this.mutateLayout(
      (layout) => {
        const stack =
          layout.drops[dropIndex]?.shelves[shelfIndex]?.stacks[stackIndex];
        if (!stack) return;

        if (direction === 'above') {
          if (productIndex === 0) return;
          stack.products.splice(0, productIndex);
          this.metrics.updateProductPositions(stack);
        } else {
          if (productIndex === stack.products.length - 1) return;
          stack.products.splice(productIndex + 1);
        }
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  // Shelf clipboard operations
  pasteShelf(dropIndex: number) {
    this.pasteShelfAt(dropIndex, null, 'end');
  }

  pasteShelfAbove(dropIndex: number, shelfIndex: number) {
    this.pasteShelfAt(dropIndex, shelfIndex, 'above');
  }

  pasteShelfBelow(dropIndex: number, shelfIndex: number) {
    this.pasteShelfAt(dropIndex, shelfIndex, 'below');
  }

  private pasteShelfAt(
    dropIndex: number,
    shelfIndex: number | null,
    position: 'end' | 'above' | 'below'
  ) {
    const clipboardShelf = this.clipboardService.getShelf();
    if (!clipboardShelf) return;

    this.mutateLayout(
      (layout) => {
        this.validationService.validatePasteShelf(
          layout,
          dropIndex,
          shelfIndex,
          clipboardShelf,
          this.planogramService.getShelfWidth(),
          position
        );

        const drop = layout.drops[dropIndex];
        if (!drop) return;
        const newShelf = cloneDeep(clipboardShelf);

        if (position === 'end') {
          drop.shelves.push(newShelf);
        } else if (position === 'above') {
          drop.shelves.splice(shelfIndex!, 0, newShelf);
        } else {
          drop.shelves.splice(shelfIndex! + 1, 0, newShelf);
        }
      },
      { drop: dropIndex }
    );
  }

  // Paste operations - Stack
  pasteStackBefore(dropIndex: number, shelfIndex: number, stackIndex: number) {
    this.pasteStackAt(dropIndex, shelfIndex, stackIndex);
  }

  pasteStackAfter(dropIndex: number, shelfIndex: number, stackIndex: number) {
    this.pasteStackAt(dropIndex, shelfIndex, stackIndex + 1);
  }

  private pasteStackAt(
    dropIndex: number,
    shelfIndex: number,
    insertAt: number
  ) {
    const clipboardStack = this.clipboardService.getStack();
    if (!clipboardStack) return;

    this.mutateLayout(
      (layout) => {
        const shelf = layout.drops[dropIndex]?.shelves[shelfIndex];
        if (!shelf) return;
        shelf.stacks.splice(insertAt, 0, cloneDeep(clipboardStack));
        this.metrics.recalculateStackPositions(layout, dropIndex, shelfIndex);
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  pasteProductProperties(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ) {
    const clipboardProduct = this.clipboardService.getProduct();
    if (!clipboardProduct) return;

    this.mutateLayout(
      (layout) => {
        const targetProduct =
          layout.drops[dropIndex]?.shelves[shelfIndex]?.stacks[stackIndex]
            ?.products[productIndex];
        if (!targetProduct) return;

        // Copy properties but keep the original product ID and position
        const { productId, position, vposition } = targetProduct;
        Object.assign(targetProduct, cloneDeep(clipboardProduct), {
          productId,
          position,
          vposition,
        });
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  /** Sets or clears dragging flag on a specific product.
   *  Mutates the LIVE layout snapshot (not a clone) and publishes without
   *  recording an undo step — preserves the original dragging semantics. */
  setProductDragging(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number,
    dragging: boolean
  ) {
    const layout = this.state.getLayoutValue();
    if (!layout) return;

    const product =
      layout.drops?.[dropIndex]?.shelves?.[shelfIndex]?.stacks?.[stackIndex]
        ?.products?.[productIndex];
    if (!product) return;

    product.dragging = dragging;

    this.publishLive(layout);
  }

  /** Sets or clears dragging flag on a specific shelf.
   *  Mutates the LIVE layout snapshot (not a clone) and publishes without
   *  recording an undo step — preserves the original dragging semantics. */
  setShelfDragging(
    dropIndex: number,
    shelfIndex: number,
    dragging: boolean
  ) {
    const layout = this.state.getLayoutValue();
    if (!layout) return;

    const shelf = layout.drops?.[dropIndex]?.shelves?.[shelfIndex];
    if (!shelf) return;

    shelf.dragging = dragging;

    this.publishLive(layout);
  }

  // ---------- Step 5b: validation-heavy mutators & misc operations ----------

  setDropSettings(index: number, settings: DropSettings) {
    this.mutateLayout(
      (draft) => {
        draft.drops.forEach((drop, dropIndex) => {
          if (index != dropIndex) return;

          drop.alignment = settings.alignment;

          drop.height = settings.height ?? this.planogramService.getDropHeight();
          drop.width = settings.width ?? this.planogramService.getShelfWidth();
          drop.depth = settings.depth ?? this.planogramService.getDropDepth();
          drop.footerHeight =
            settings.footerHeight ?? this.planogramService.getDropFooterHeight();
          drop.lipHeight =
            settings.lipHeight ?? this.planogramService.getShelfLipHeight();
          drop.minSpacing =
            settings.minSpacing ?? this.planogramService.getShelfMinimumSpacing();

          drop.shelves.forEach((shelf, shelfIndex) => {
            let shelfSettings = settings.shelfSettings.find(
              (p) => p.index == shelfIndex
            );
            if (shelfSettings) {
              shelf.shelfDepth = shelfSettings.depth;
              shelf.minHeight = shelfSettings.minHeight;
              shelf.text = shelfSettings.text;
            }
          });
        });
      },
      { skipUndo: true }
    );
  }

  setLayout(layout: PlanogramLayout) {
    layout.drops.forEach((drop, dropIndex) => {
      drop.shelves.forEach((shelf) => {
        shelf.stacks.forEach((stack) => {
          stack.products.forEach((product) => {
            let productRef = this.state.planogramItems.get(product.productId);
            if (productRef) {
              product.dimensions =
                this.layoutUtils.calculateEffectiveDimensions(
                  productRef,
                  product
                );
            }
          });
        });
      });
      this.metrics.updateDropShelfSpacing(layout, dropIndex);
    });
    this.state.planogramItems = layout.productReference;

    this.metrics.updateLayoutBreakdown(layout);
    this.metrics.updateLayoutDOSBreakdown(layout);
    this.metrics.updateLayoutCapacityBreakdown(layout);

    this.state.publishLayout(layout);
    this.state.publishProductsChanged();
    this.history.clear();
  }

  removeProducts(products: number[]) {
    if (!this.state.getLayoutValue()) return;
    for (let i = 0; i < products.length; i++) {
      this.state.planogramItems.delete(products[i]);
    }
    this.mutateLayout((draft) => {
      for (let i = 0; i < products.length; i++) {
        draft.productReference.delete(products[i]);
      }
    });
    this.state.publishProductsChanged();
  }

  importProducts(products: ProductReference[]) {
    if (!this.state.getLayoutValue()) return;
    for (let i = 0; i < products.length; i++) {
      this.state.planogramItems.set(products[i].productId, products[i]);
    }
    this.mutateLayout((draft) => {
      for (let i = 0; i < products.length; i++) {
        draft.productReference.set(products[i].productId, products[i]);
      }
    });
    this.state.publishProductsChanged();
  }

  refreshProducts(products: ProductReference[], fields: string[]): boolean {
    const current = this.state.getLayoutValue();
    if (!current) return false;

    // New entries (e.g. alternate-configuration rows the backend now surfaces
    // alongside refresh) get imported wholesale instead of running through the
    // field-by-field merge — there's nothing to merge into yet.
    const newProducts: ProductReference[] = [];
    const refreshable: ProductReference[] = [];
    for (const product of products) {
      if (this.state.planogramItems.get(product.productId)) refreshable.push(product);
      else newProducts.push(product);
    }
    if (newProducts.length > 0) this.importProducts(newProducts);

    let dimensionsChanged = false;
    this.mutateLayout((layout) => {

    for (let i = 0; i < refreshable.length; i++) {
      let product = refreshable[i];
      let reference = this.state.planogramItems.get(product.productId);
      if (!reference) return;

      product.hFacings = reference.hFacings ?? 1;
      product.vFacings = reference.vFacings ?? 1;
      product.dFacings = reference.dFacings ?? 1;

      if (
        !dimensionsChanged &&
        (product.effectiveDimensions.width !=
          reference.effectiveDimensions.width ||
          product.effectiveDimensions.height !=
            reference.effectiveDimensions.height)
      )
        dimensionsChanged = true;

      const updatedProduct: ProductReference = { ...reference };
      fields.forEach((field) => {
        switch (field) {
          case 'Description':
            updatedProduct.description = product.description;
            break;
          case 'Unit':
            updatedProduct.unit = product.unit;
            break;
          case 'Uos':
            updatedProduct.uos = product.uos;
            break;
          case 'Manufacturer':
            updatedProduct.manufacturer = product.manufacturer;
            break;
          case 'Brand':
            updatedProduct.brand = product.brand;
            break;
          case 'Category':
            updatedProduct.categoryLevel1 = product.categoryLevel1;
            break;
          case 'Segment':
            updatedProduct.categoryLevel2 = product.categoryLevel2;
            break;
          case 'Family':
            updatedProduct.categoryLevel3 = product.categoryLevel3;
            break;
          case 'Base':
            updatedProduct.categoryLevel4 = product.categoryLevel4;
            break;
          case 'FlowOrder':
            updatedProduct.categoryFlow = product.categoryFlow;
            break;
          case 'Dimensions':
            updatedProduct.dimensions = product.dimensions;
            updatedProduct.effectiveDimensions = product.effectiveDimensions;
            break;
          case 'Orientation':
            updatedProduct.orientation = product.orientation;
            break;
          case 'ShelfPosition':
            updatedProduct.shelfPosition = product.shelfPosition;
            break;
          case 'SqueezeFactor':
            updatedProduct.squeezeW = product.squeezeW;
            break;
          case 'Labels':
            updatedProduct.labels = product.labels;
            break;
          case 'Price':
            if (updatedProduct.salesData && product.salesData) {
              updatedProduct.salesData.currentPrice =
                product.salesData.currentPrice;
              updatedProduct.salesData.averagePrice =
                product.salesData.averagePrice;
            }
            break;
          case 'SalesData':
            if (product.salesData) updatedProduct.salesData = product.salesData;
            break;
          case 'Status':
            updatedProduct.statusId = product.statusId;
            break;
          case 'Ranged':
            updatedProduct.ranged = product.ranged;
            break;
          case 'CasePack':
            updatedProduct.casePack = product.casePack;
            break;
        }
      });

      this.state.planogramItems.set(product.productId, updatedProduct);
      layout.productReference.set(product.productId, updatedProduct);
    }

    if (dimensionsChanged) {
      layout.drops.forEach((drop, dropIndex) => {
        drop.shelves.forEach((shelf, shelfIndex) => {
          shelf.stacks.forEach((stack, stackIndex) => {
            stack.products.forEach((product, productIndex) => {
              let reference = layout.productReference.get(product.productId);
              if (reference) {
                product.dimensions =
                  this.layoutUtils.calculateEffectiveDimensions(
                    reference,
                    product
                  );
              }
            });
          });
        });
      });
    }

    });
    this.state.publishProductsChanged();
    return dimensionsChanged;
  }

  flagProductCaptureRequired(productId: number) {
    this.mutateLayout((layout) => {
      const reference = layout.productReference.get(productId);
      if (reference) {
        reference.captureRequired = true;
      }
    });

    const item = this.state.planogramItems.get(productId);
    if (item) {
      item.captureRequired = true;
    }

    this.state.publishProductsChanged();
  }

  removeProductFromLayout(productId: number) {
    this.bulkRemoveProductsFromLayout([productId]);
  }

  bulkRemoveProductsFromLayout(productIds: number[]) {
    if (!productIds || productIds.length === 0) return;
    if (!this.state.getLayoutValue()) return;

    const idSet = new Set(productIds);

    this.mutateLayout((layout) => {
      layout.drops.forEach((drop, dropIndex) => {
        drop.shelves.forEach((shelf, shelfIndex) => {
          for (let stackIndex = shelf.stacks.length - 1; stackIndex >= 0; stackIndex--) {
            const stack = shelf.stacks[stackIndex];
            stack.products = stack.products.filter(p => !idSet.has(p.productId));
            if (stack.products.length === 0) {
              shelf.stacks.splice(stackIndex, 1);
            } else {
              this.metrics.updateProductPositions(stack);
            }
          }
          this.metrics.recalculateStackPositions(layout, dropIndex, shelfIndex);
          this.metrics.updateShelfMetrics(layout, dropIndex, shelfIndex);
        });
        this.metrics.updateDropMetrics(layout, dropIndex);
      });
    });
    this.state.publishProductsChanged();
  }

  copyDrop(dropIndex: number) {
    const layout = this.query.getCurrentLayout();
    if (!layout) return;

    const drop = layout.drops[dropIndex];
    if (!drop) return;

    this.clipboardService.copyDrop(cloneDeep(drop));
  }

  pasteDropBefore(dropIndex: number) {
    const clipboardDrop = this.clipboardService.getDrop();
    if (!clipboardDrop) return;

    this.mutateLayout(
      (layout) => {
        layout.drops.splice(dropIndex, 0, cloneDeep(clipboardDrop));
      },
      { drop: dropIndex }
    );
  }

  pasteDropAfter(dropIndex: number) {
    const clipboardDrop = this.clipboardService.getDrop();
    if (!clipboardDrop) return;

    this.mutateLayout((layout) => {
      const newDrop = cloneDeep(clipboardDrop);
      newDrop.shelves.forEach((shelf, shelfIndex) => {
        this.metrics.updateShelfMetrics(layout, dropIndex + 1, shelfIndex);
      });
      layout.drops.splice(dropIndex + 1, 0, newDrop);
      this.metrics.updateDropMetrics(layout, dropIndex + 1);
    });
  }

  addShelf(
    dropIndex: number,
    shelfIndex: number,
    newShelf: PlanogramShelf | null = null,
    skipValidation: boolean = false
  ) {
    if (!this.state.getLayoutValue()) return;

    const shelfToInsert: PlanogramShelf = newShelf ?? {
      stacks: [],
      topShelf: false,
      filledWidth: 0,
      shelfHeight: 0,
      shelfSpacing: 0,
      shelfType: ShelfType.REGULAR,
    };

    this.mutateLayout((layout) => {
      if (!skipValidation) {
        const drop = layout.drops[dropIndex];
        if (!drop) throw new Error(`Drop ${dropIndex} not found`);

        const testDrop = { ...drop, shelves: [...drop.shelves] };
        testDrop.shelves.splice(shelfIndex, 0, shelfToInsert);

        this.validationService.validateDropHeight(testDrop, layout);
      }

      layout.drops[dropIndex].shelves.splice(shelfIndex, 0, shelfToInsert);
    });
  }

  moveShelf(
    sourceDropIndex: number,
    sourceShelfIndex: number,
    targetDropIndex: number,
    targetShelfIndex: number
  ) {
    if (!this.state.getLayoutValue()) return;

    this.mutateLayout((layout) => {
      this.validationService.validateShelfMovement(
        layout,
        sourceDropIndex,
        sourceShelfIndex,
        targetDropIndex,
        targetShelfIndex
      );

      const sourceDrop = layout.drops[sourceDropIndex];
      const targetDrop = layout.drops[targetDropIndex];
      if (!sourceDrop || !targetDrop) return;

      const [shelfToMove] = sourceDrop.shelves.splice(sourceShelfIndex, 1);
      if (!shelfToMove) return;
      shelfToMove.dragging = false;

      targetDrop.shelves.splice(targetShelfIndex, 0, shelfToMove);

      this.metrics.updateDropShelfSpacing(layout, sourceDropIndex);
      this.metrics.updateDropShelfSpacing(layout, targetDropIndex);
    });
  }

  /** Adds a product to an existing stack at the specified position */
  addProductToStack(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number,
    product: PlanogramProduct,
    skipValidation: boolean = false
  ) {
    if (!this.state.getLayoutValue()) return;

    let productRef = this.state.planogramItems.get(product.productId);
    if (productRef)
      product.dimensions =
        this.layoutUtils.calculateEffectiveDimensions(productRef, product);

    this.mutateLayout(
      (layout) => {
        if (!skipValidation)
          this.validationService.validateAddProductToStack(
            layout,
            dropIndex,
            shelfIndex,
            stackIndex,
            product,
            this.state.planogramItems
          );

        const shelf = layout.drops[dropIndex].shelves[shelfIndex];
        const stack = shelf.stacks[stackIndex];

        if (!stack) {
          throw new Error(`Stack at index ${stackIndex} does not exist`);
        }

        stack.products.splice(productIndex, 0, product);

        this.metrics.updateProductPositions(stack, true);
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  /** Creates a new stack with the product at the specified stack position */
  addProductAsNewStack(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    product: PlanogramProduct,
    skipValidation: boolean = false
  ) {
    if (!this.state.getLayoutValue()) return;

    let productRef = this.state.planogramItems.get(product.productId);
    if (productRef)
      product.dimensions =
        this.layoutUtils.calculateEffectiveDimensions(productRef, product);

    this.mutateLayout(
      (layout) => {
        if (!skipValidation)
          this.validationService.validateProductPlacement(
            layout,
            dropIndex,
            shelfIndex,
            product,
            this.state.planogramItems,
            this.planogramService.getShelfWidth()
          );

        const shelf = layout.drops[dropIndex].shelves[shelfIndex];

        const newStack: PlanogramProductStack = {
          position: 0,
          products: [product],
        };

        shelf.stacks.splice(stackIndex, 0, newStack);

        product.vposition = 0;

        this.metrics.recalculateStackPositions(layout, dropIndex, shelfIndex);
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  moveProduct(
    sourceDropIndex: number,
    sourceShelfIndex: number,
    sourceStackIndex: number,
    sourceProductIndex: number,
    targetDropIndex: number,
    targetShelfIndex: number,
    targetStackIndex: number | null,
    asNewStack: boolean = false,
    targetProductIndex: number = 0
  ) {
    const current = this.state.getLayoutValue();
    if (!current) return;

    const currentSourceShelf =
      current.drops[sourceDropIndex]?.shelves[sourceShelfIndex];
    const currentTargetShelf =
      current.drops[targetDropIndex]?.shelves[targetShelfIndex];
    if (!currentSourceShelf || !currentTargetShelf) return;

    this.mutateLayout((layout) => {
      const sourceShelf = layout.drops[sourceDropIndex].shelves[sourceShelfIndex];
      const targetShelf = layout.drops[targetDropIndex].shelves[targetShelfIndex];

      const sourceStack = sourceShelf.stacks[sourceStackIndex];
      if (!sourceStack || sourceProductIndex >= sourceStack.products.length)
        return;

      const productToMove = sourceStack.products[sourceProductIndex];
      if (!productToMove) return;

      this.validationService.validateProductMovement(
        layout,
        sourceDropIndex,
        sourceShelfIndex,
        sourceProductIndex,
        targetDropIndex,
        targetShelfIndex,
        targetStackIndex ?? 0,
        this.planogramService.getShelfWidth()
      );

      sourceStack.products.splice(sourceProductIndex, 1);

      if (sourceStack.products.length === 0) {
        sourceShelf.stacks.splice(sourceStackIndex, 1);
      } else {
        this.metrics.updateProductPositions(sourceStack);
      }

      productToMove.dragging = false;

      if (
        !asNewStack &&
        targetStackIndex !== null &&
        targetShelf.stacks[targetStackIndex]
      ) {
        const targetStack = targetShelf.stacks[targetStackIndex];
        const insertPos = Math.min(
          targetProductIndex,
          targetStack.products.length
        );
        targetStack.products.splice(insertPos, 0, productToMove);
        this.metrics.updateProductPositions(targetStack, true);
      } else {
        const newStack: PlanogramProductStack = {
          position: 0,
          products: [productToMove],
        };
        targetShelf.stacks.splice(
          targetStackIndex ?? targetShelf.stacks.length,
          0,
          newStack
        );
        productToMove.vposition = 0;
        this.metrics.recalculateStackPositions(
          layout,
          targetDropIndex,
          targetShelfIndex
        );
      }

      if (
        sourceDropIndex !== targetDropIndex ||
        sourceShelfIndex !== targetShelfIndex
      ) {
        this.metrics.recalculateStackPositions(
          layout,
          sourceDropIndex,
          sourceShelfIndex
        );
      }

      this.metrics.updateShelfMetrics(layout, sourceDropIndex, sourceShelfIndex);
      this.metrics.updateDropMetrics(layout, sourceDropIndex);
      this.metrics.updateShelfMetrics(layout, targetDropIndex, targetShelfIndex);
      this.metrics.updateDropMetrics(layout, targetDropIndex);
    });
  }

  addHFacing(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ) {
    const current = this.state.getLayoutValue();
    if (!current) return;

    const currentProduct =
      current.drops[dropIndex]?.shelves[shelfIndex]?.stacks[stackIndex]
        ?.products[productIndex];
    if (!currentProduct) return;

    const reference = this.state.planogramItems.get(currentProduct.productId);
    if (!reference) return;

    this.mutateLayout(
      (layout) => {
        const drop = layout.drops[dropIndex];
        const shelf = drop.shelves[shelfIndex];
        const sourceStack = shelf.stacks[stackIndex];
        const product = sourceStack.products[productIndex];

        const newProduct = cloneDeep(product);
        newProduct.dragging = false;

        this.validationService.validateProductPlacement(
          layout,
          dropIndex,
          shelfIndex,
          newProduct,
          this.state.planogramItems,
          drop.width ?? this.planogramService.getShelfWidth()
        );

        shelf.stacks.splice(stackIndex + 1, 0, { position: 0, products: [newProduct] });

        this.metrics.recalculateStackPositions(layout, dropIndex, shelfIndex);
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  duplicateStack(dropIndex: number, shelfIndex: number, stackIndex: number) {
    const current = this.state.getLayoutValue();
    if (!current) return;
    const currentStack =
      current.drops[dropIndex]?.shelves[shelfIndex]?.stacks[stackIndex];
    if (!currentStack) return;

    this.mutateLayout(
      (layout) => {
        const shelf = layout.drops[dropIndex].shelves[shelfIndex];
        const sourceStack = shelf.stacks[stackIndex];
        shelf.stacks.splice(stackIndex + 1, 0, cloneDeep(sourceStack));
        this.metrics.recalculateStackPositions(layout, dropIndex, shelfIndex);
      },
      { drop: dropIndex, shelf: shelfIndex }
    );
  }

  pasteProductBefore(d: number, s: number, k: number) {
    const clipboardProduct = this.clipboardService.getProduct();
    if (!clipboardProduct) return;
    this.addProductAsNewStack(d, s, k, cloneDeep(clipboardProduct));
  }

  pasteProductAfter(d: number, s: number, k: number) {
    const clipboardProduct = this.clipboardService.getProduct();
    if (!clipboardProduct) return;
    this.addProductAsNewStack(d, s, k + 1, cloneDeep(clipboardProduct));
  }

  pasteProductAbove(d: number, s: number, k: number, p: number) {
    const clipboardProduct = this.clipboardService.getProduct();
    if (!clipboardProduct) return;
    this.addProductToStack(d, s, k, p, cloneDeep(clipboardProduct));
  }

  pasteProductBelow(d: number, s: number, k: number, p: number) {
    const clipboardProduct = this.clipboardService.getProduct();
    if (!clipboardProduct) return;
    this.addProductToStack(d, s, k, p + 1, cloneDeep(clipboardProduct));
  }

  pasteProductBeforeStack(d: number, s: number, k: number) {
    const clipboardProduct = this.clipboardService.getProduct();
    if (!clipboardProduct) return;
    this.addProductAsNewStack(d, s, k, cloneDeep(clipboardProduct));
  }

  pasteProductAfterStack(d: number, s: number, k: number) {
    const clipboardProduct = this.clipboardService.getProduct();
    if (!clipboardProduct) return;
    this.addProductAsNewStack(d, s, k + 1, cloneDeep(clipboardProduct));
  }

  // ---------- Floating shelf ----------

  /** Adds a product to the floating shelf tray. De-duplicates by product id;
   *  new entries default to a capacity of 1 and are appended to the end. */
  addProductToFloatingShelf(productId: number) {
    this.mutateLayout((layout) => {
      if (!layout.floatingShelf) layout.floatingShelf = [];
      if (layout.floatingShelf.some((p) => p.productId === productId)) return;
      layout.floatingShelf.push({
        productId,
        capacity: 1,
        sortOrder: layout.floatingShelf.length,
      });
    });
  }

  /** Removes a product from the floating shelf tray and re-numbers sort order. */
  removeProductFromFloatingShelf(productId: number) {
    this.mutateLayout((layout) => {
      if (!layout.floatingShelf) return;
      layout.floatingShelf = layout.floatingShelf.filter(
        (p) => p.productId !== productId
      );
      layout.floatingShelf.forEach((p, i) => (p.sortOrder = i));
    });
  }

  /** Updates the assigned capacity for a product on the floating shelf. */
  setFloatingShelfCapacity(productId: number, capacity: number) {
    this.mutateLayout((layout) => {
      const entry = layout.floatingShelf?.find((p) => p.productId === productId);
      if (!entry) return;
      entry.capacity = Math.max(0, Math.floor(capacity));
    });
  }

  clearLayout() {
    this.state.publishLayout(null);
    this.history.clear();
  }
}
